import { type JSX, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { shortMonthAndDay } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader, Banner } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { getCompareStripedCircleGradient } from "app/pages/industry-analysis/players/utils/getCompareStripedCircle";
import DurationService from "app/services/DurationService";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { subtitleFiltersWithCustomColors, useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import {
    TOTAL_KEYWORD_LIST_COLOR,
    OTHER_KEYWORD_LIST_COLOR,
    useKeywordLists,
} from "app/modules/demand-analysis/keyword-lists";
import { SearchVolumeOverTimeTotalVolumeDescription } from "app/pages/market-research/features/search-volume-over-time-widget";

import { Divider } from "../../components/Divider";
import { useMonthToDateToggle } from "../../hooks/useMonthToDateToggle";

import { KeywordsTrendsWidgetColumnChart } from "./KeywordsTrendsWidgetColumnChart";
import { KeywordsTrendsWidgetLegend } from "./KeywordsTrendsWidgetLegend";
import { KeywordsTrendsWidgetLineChart } from "./KeywordsTrendsWidgetLineChart";
import { type KeywordGroupComparisonLegend, useKeywordListData } from "./useKeywordListData";

const LegendsWrapper = styled.div`
    width: 100%;
    box-sizing: border-box;
    display: grid;
    grid-auto-rows: 1fr;
    grid-template-columns: repeat(6, minmax(min-content, 1fr));
    gap: 24px;

    @media (min-width: 1440px) {
        grid-template-columns: repeat(5, minmax(min-content, max-content)) 1fr;
    }
`;

const CHARTS_WRAPPER_HEIGHT = 250;
const COLUMN_CHART_HEIGHT = 228;

const ChartsWrapper = styled.div`
    display: grid;
    width: 100%;
    gap: 24px;
    height: ${CHARTS_WRAPPER_HEIGHT}px;
    grid-template-columns: 160px 1fr;
    grid-template-areas: "columnChart lineChart";
`;

const ColumnChartWrapper = styled.div`
    grid-area: columnChart;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 160px;
    height: 250px;

    > p {
        margin-top: 8px;
        margin-bottom: 0;
    }
`;

const LineChartWrapper = styled.div`
    grid-area: lineChart;
`;

const WidgetContainerWrapper = styled(WidgetContainer)`
    width: 100%;
    overflow: clip;
`;

export const KeywordsTrendsWidget = (): JSX.Element => {
    const { params } = useCurrentStateAndParams();
    const { isWindow } = DurationService.getDurationData(params.duration).raw;
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();
    const t = useTranslation();
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const enrichedSubtitleFilters = isPeriodOverPeriod
        ? subtitleFiltersWithCustomColors(
              subtitleFilters,
              colorsPalettes.carbon[200],
              getCompareStripedCircleGradient(colorsPalettes.carbon[200]),
          )
        : subtitleFilters;
    const { getKeywordListById } = useKeywordLists();
    const { isMonthToDateActive, onOffSwitchConfig } = useMonthToDateToggle({
        eventSubName: "Search Volume Over Time",
        mtdParam: "searchVolumeMTD",
    });
    const { isLoading, isError, data } = useKeywordListData({ isMonthToDate: isMonthToDateActive });
    const legendItems = data.legendItems.map((legend) => ({
        ...legend,
        color: getKeywordListById(legend.id)?.color ?? OTHER_KEYWORD_LIST_COLOR,
    }));
    const legendTotal = {
        ...data.legendTotal,
        name: t("common.total"),
        color: TOTAL_KEYWORD_LIST_COLOR,
    };
    const [enabledKeywordGroups, setEnabledKeywordGroups] = useState<Set<string | symbol>>(
        new Set(legendItems.map((legend) => legend.id)),
    );

    const handleLegendClick = (legend: KeywordGroupComparisonLegend) => {
        const newEnabledKeywordGroups = new Set(enabledKeywordGroups);

        if (newEnabledKeywordGroups.has(legend.id)) {
            newEnabledKeywordGroups.delete(legend.id);
        } else {
            newEnabledKeywordGroups.add(legend.id);
        }

        setEnabledKeywordGroups(newEnabledKeywordGroups);
    };

    return (
        <WidgetContainerWrapper
            withBorder
            automationId="search-trends-overview-widget"
            noData={isError}
            title={translate("demand.analysis.search.trends.trends.overview.header.title")}
            tooltip={translate("demand.analysis.search.trends.demand.over.time.volume.over.time.header.title.tooltip")}
            subtitleFilters={enrichedSubtitleFilters}
            onOffSwitchConfig={onOffSwitchConfig}
        >
            <Stack
                padding="24px"
                gap="24px"
            >
                <LegendsWrapper>
                    {legendItems.map((legend) => (
                        <KeywordsTrendsWidgetLegend
                            key={String(legend.id)}
                            isLoading={isLoading}
                            isEnabled={enabledKeywordGroups.has(legend.id)}
                            legend={legend}
                            onClick={handleLegendClick}
                        />
                    ))}
                    <Flex
                        style={{ gridColumn: "6" }}
                        flexDirection="row"
                        flexWrap="nowrap"
                        justifyContent="flex-end"
                    >
                        <Divider
                            vertical
                            margin="0 16px 0 0"
                        />
                        <KeywordsTrendsWidgetLegend
                            isTooltipDisabled
                            isLoading={isLoading}
                            isEnabled={enabledKeywordGroups.has(legendTotal.id)}
                            legend={legendTotal}
                            onClick={handleLegendClick}
                        />
                    </Flex>
                </LegendsWrapper>
                {isMonthToDateActive ? (
                    <Banner
                        title=""
                        subtitle={
                            <Text fontVariant="body1">
                                {translate("demand_analysis.search_volume_over_time.month_to_date_banner.title", {
                                    untilDate: dayjs(swSettings.current.windowEndDate).format(shortMonthAndDay),
                                })}
                            </Text>
                        }
                        CustomIcon={
                            <SWReactIcons
                                size="xs"
                                iconName="info"
                                iconColor={colorsPalettes.carbon[200]}
                            />
                        }
                    />
                ) : null}
                <ChartsWrapper>
                    <ColumnChartWrapper>
                        {isLoading ? (
                            <PixelPlaceholderLoader
                                width={160}
                                height={250}
                            />
                        ) : (
                            <KeywordsTrendsWidgetColumnChart
                                enabledKeywordGroups={enabledKeywordGroups}
                                height={COLUMN_CHART_HEIGHT}
                                data={data}
                            />
                        )}
                        {!isLoading && !isError && enabledKeywordGroups.size !== 0 ? (
                            <SearchVolumeOverTimeTotalVolumeDescription />
                        ) : null}
                    </ColumnChartWrapper>
                    <LineChartWrapper>
                        {isLoading ? (
                            <PixelPlaceholderLoader
                                width="100%"
                                height={250}
                            />
                        ) : (
                            <KeywordsTrendsWidgetLineChart
                                enabledKeywordGroups={enabledKeywordGroups}
                                height={CHARTS_WRAPPER_HEIGHT}
                                timeGranularity={isWindow ? "Daily" : "Monthly"}
                                data={data}
                            />
                        )}
                    </LineChartWrapper>
                </ChartsWrapper>
            </Stack>
        </WidgetContainerWrapper>
    );
};
